import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Button,
  Card,
  CardContent,
  Stack,
  TextField,
  Typography,
  CircularProgress,
  IconButton,
} from "@mui/material";
import { useFormik, Form, FormikProvider } from "formik";
import * as Yup from "yup";
import { EMAIL_REGEX } from "../../utils/constants";
import apiUsers from "../../services/authServiceLogin";
import errorHandler from "../../utils/errorHandler";
import { toast } from "react-toastify";
import PhoneInput from "react-phone-input-2";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";

const URL_REGEX = /^https:\/\/[a-zA-Z0-9-]+(\.[a-zA-Z]{2,})+(\/)?$/;

function GlobalSetting() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const settingData = async () => {
    try {
      setLoading(true);
      const response = await apiUsers.getSetting();
      setData(response.data.data);
    } catch (error) {
      errorHandler(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    settingData();
  }, []);


  const initialValues = {
    instaLink: data?.instaLink ?? "",
    fbLink: data?.fbLink ?? "",
    telegramLink: data?.telegramLink ?? "",
    contactUsEmail: data?.contactUsEmail ?? "",
    commission: data?.commission ?? "",
    deliveryFee: data?.deliveryFee ?? 0,
    rewardPoints: data?.rewardPoints ?? "",
    rewardExpiry: data?.rewardExpiry ?? "",
    rewardThresholdValue: data?.rewardThresholdValue || "",
    contactUs: data?.contactUs && data?.contactUs.length > 0 ? data.contactUs : [],
    dealerOrderRange: data?.dealerOrderRange ?? "",
    companyAddress: data?.companyAddress ?? "",
  };

  const validationSchema = Yup.object().shape({
    instaLink: Yup.string()
      .trim()
      .matches(
        URL_REGEX,
        "Instagram link must be in the format: https://<domain>.com/"
      )
      .max(100, "Instagram link must be at most 100 characters")
      .required("Instagram Link is required"),
    fbLink: Yup.string()
      .trim()
      .matches(
        URL_REGEX,
        "Facebook link must be in the format: https://<domain>.com/"
      )
      .max(100, "Facebook link must be at most 100 characters")
      .required("Facebook Link is required"),
    contactUsEmail: Yup.string()
      .required("Contact Us Email is required")
      .trim()
      .matches(EMAIL_REGEX, "Please enter a valid email address")
      .max(255),
    commission: Yup.string()
      .required("Commission is required")
      .matches(/^\d+(\.\d+)?$/, "Commission must be a valid number")
      .test("min-max", "Commission must be between 0.01 and 100", (value) => {
        const numValue = parseFloat(value);
        return numValue > 0 && numValue <= 99.99;
      })
      .nullable(),
    dealerOrderRange: Yup.string()
      .required("Dealer Order Range is required")
      .matches(/^\d+$/, "Must be a valid number (in meters)")
      .max(15, "Maximum 15 digits allowed"),
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema,
    onSubmit: (values, { setSubmitting }) => {
      values.instaLink = values?.instaLink?.trim();
      values.fbLink = values?.fbLink?.trim();
      values.contactUsEmail = values?.contactUsEmail?.trim();
      values.rewardPoints = parseFloat(values.rewardPoints || 0);

      apiUsers
        .updateSetting(values)
        .then((res) => {
          if (res.status === 200) {
            toast.success(res?.data?.message);
          }
          setSubmitting(false);
          settingData();
        })
        .catch((err) => {
          errorHandler(err);
          setSubmitting(false);
        });
    },
  });

  const { errors, touched, isSubmitting, handleSubmit, getFieldProps } = formik;

  return (
    <Card>
      <CardContent>
        <FormikProvider value={formik} style={{ padding: "34px 30px" }}>
          <Typography variant="h4" sx={{ mb: 2 }} align="start">
            Settings
          </Typography>
          {loading ? (
            <CircularProgress />
          ) : (
            <Form>
              <Stack spacing={3}>
                <TextField
                  name="instaLink"
                  label="Instagram Link *"
                  fullWidth
                  {...getFieldProps("instaLink")}
                  error={Boolean(touched.instaLink && errors.instaLink)}
                  helperText={touched.instaLink && errors.instaLink}
                />

                <TextField
                  name="fbLink"
                  label="Facebook Link *"
                  {...getFieldProps("fbLink")}
                  error={Boolean(touched.fbLink && errors.fbLink)}
                  helperText={touched.fbLink && errors.fbLink}
                />

                <TextField
                  name="telegramLink"
                  label="Telegram Support *"
                  {...getFieldProps("telegramLink")}
                />

                <TextField
                  name="contactUsEmail"
                  label="Contact Us Email *"
                  {...getFieldProps("contactUsEmail")}
                  error={Boolean(
                    touched.contactUsEmail && errors.contactUsEmail
                  )}
                  helperText={touched.contactUsEmail && errors.contactUsEmail}
                />
                <TextField
                  name="companyAddress"
                  label="Company Address"
                  {...getFieldProps("companyAddress")}
                />

                <Stack spacing={2}>
                  <Typography variant="subtitle1">Contact Us Numbers</Typography>
                  {(formik.values.contactUs.length === 0 ? [""] : formik.values.contactUs).map((phone, index) => (
                    <Stack key={index} direction="row" spacing={2} alignItems="center">
                      <PhoneInput
                        country={"kh"}
                        value={phone}
                        onChange={(value) => {
                          const updatedContacts = [...formik.values.contactUs];
                          // When initial array was empty, updatedContacts may be empty, so handle carefully:
                          if (updatedContacts.length === 0) {
                            updatedContacts.push(value);
                          } else {
                            updatedContacts[index] = value;
                          }
                          formik.setFieldValue("contactUs", updatedContacts);
                        }}
                        inputProps={{
                          name: `contactUs[${index}]`,
                          required: true,
                        }}
                        inputStyle={{ width: "100%", height: "56px" }}
                      />
                      {formik.values.contactUs.length > 1 && (
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => {
                            const updatedContacts = [...formik.values.contactUs];
                            updatedContacts.splice(index, 1);
                            formik.setFieldValue("contactUs", updatedContacts);
                          }}
                        >
                          <RemoveIcon />
                        </IconButton>
                      )}
                      {index === formik.values.contactUs.length - 1 && (
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => {
                            const updatedContacts = [...formik.values.contactUs, ""];
                            formik.setFieldValue("contactUs", updatedContacts);
                          }}
                        >
                          <AddIcon />
                        </IconButton>
                      )}
                    </Stack>
                  ))}

                </Stack>

                <TextField
                  name="commission"
                  label="Dealer Commission (%) *"
                  {...getFieldProps("commission")}
                  error={Boolean(touched.commission && errors.commission)}
                  helperText={touched.commission && errors.commission}
                  inputProps={{
                    pattern: "^[0-9]*([.][0-9]{0,2})?$",
                    inputMode: "decimal",
                    maxLength: 8,
                  }}
                />

                <TextField
                  name="deliveryFee"
                  label="Delivery Fee"
                  {...getFieldProps("deliveryFee")}
                  error={Boolean(touched.deliveryFee && errors.deliveryFee)}
                  helperText={touched.deliveryFee && errors.deliveryFee}
                  inputProps={{
                    pattern: "^[0-9]*([.][0-9]{0,2})?$",
                    inputMode: "decimal",
                    maxLength: 10,
                  }}
                />

                <TextField
                  name="rewardPoints"
                  label="Reward Points"
                  {...getFieldProps("rewardPoints")}
                  error={Boolean(touched.rewardPoints && errors.rewardPoints)}
                  helperText={touched.rewardPoints && errors.rewardPoints}
                  inputProps={{
                    pattern: "^[0-9]*([.][0-9]{0,2})?$",
                    inputMode: "decimal",
                    maxLength: 7,
                  }}

                />

                <TextField
                  name="rewardExpiry"
                  label="Reward Expiry"
                  {...getFieldProps("rewardExpiry")}
                  error={Boolean(touched.rewardExpiry && errors.rewardExpiry)}
                  helperText={touched.rewardExpiry && errors.rewardExpiry}
                  inputProps={{
                    inputMode: "numeric",
                    maxLength: 3,
                  }}
                  onKeyDown={(e) => {
                    if (
                      !/[0-9]/.test(e.key) &&
                      e.key !== "Backspace" &&
                      e.key !== "Delete" &&
                      e.key !== "ArrowLeft" &&
                      e.key !== "ArrowRight"
                    ) {
                      e.preventDefault();
                    }
                  }}
                />
                <TextField
                  name="rewardThresholdValue"
                  label="Reward Threshold Value"
                  {...getFieldProps("rewardThresholdValue")}
                  error={Boolean(
                    touched.rewardThresholdValue && errors.rewardThresholdValue
                  )}
                  helperText={
                    touched.rewardThresholdValue && errors.rewardThresholdValue
                  }
                  inputProps={{
                    inputMode: "numeric",
                    maxLength: 3,
                  }}
                  onKeyDown={(e) => {
                    if (
                      !/[0-9]/.test(e.key) &&
                      e.key !== "Backspace" &&
                      e.key !== "Delete" &&
                      e.key !== "ArrowLeft" &&
                      e.key !== "ArrowRight"
                    ) {
                      e.preventDefault();
                    }
                  }}
                />

                <TextField
                  name="dealerOrderRange"
                  label="Dealer Order Range (in meters) *"
                  {...getFieldProps("dealerOrderRange")}
                  error={Boolean(
                    touched.dealerOrderRange && errors.dealerOrderRange
                  )}
                  helperText={
                    touched.dealerOrderRange && errors.dealerOrderRange
                  }
                  inputProps={{
                    inputMode: "numeric",
                    maxLength: 5,
                  }}
                  onKeyDown={(e) => {
                    if (
                      !/[0-9]/.test(e.key) &&
                      e.key !== "Backspace" &&
                      e.key !== "Delete" &&
                      e.key !== "ArrowLeft" &&
                      e.key !== "ArrowRight"
                    ) {
                      e.preventDefault();
                    }
                  }}
                />
              </Stack>

              <Button
                size="large"
                type="submit"
                variant="contained"
                disabled={isSubmitting}
                sx={{ my: 2 }}
              >
                Update
              </Button>
              <Button
                size="large"
                variant="contained"
                onClick={() => {
                  if (!isSubmitting) navigate("/dashboard");
                }}
                sx={{ my: 2, ml: 4 }}
              >
                Cancel
              </Button>
            </Form>
          )}
        </FormikProvider>
      </CardContent>
    </Card>
  );
}

export default GlobalSetting;
